import { useCallback } from 'react'

import { UploadFile } from 'types'
import { focusElementById } from 'utils/events'
import { CustomFormQuestion } from 'components/tools/CustomForm/types'
import validateCEPOnCustomForm from '../../utils/validateCEPOnCustomForm'
import validateDateOnCustomForm from '../../utils/validateDateOnCustomForm'
import validateCPFOnCustomForm from '../../utils/validateCPFOnCustomForm'
import validateCTPSOnCustomForm from '../../utils/validateCTPSOnCustomForm'
import validatePISOnCustomForm from '../../utils/validatePISOnCustomForm'
import validateHourOnCustomForm from '../../utils/validateHourOnCustomForm'
import validateSingleFileUploadOnCustomForm from '../../utils/validateSingleFileUploadOnCustomForm'
import validateMultipleFileUploadOnCustomForm from '../../utils/validateMultipleFileUploadOnCustomForm'
import validateEntityOnCustomForm from '../../utils/validateEntityOnCustomForm'
import validateTextOnCustomForm from '../../utils/validateTextOnCustomForm'
import validateLongTextOnCustomForm from '../../utils/validateLongTextOnCustomForm'
import validateMailOnCustomForm from '../../utils/validateMailOnCustomForm'
import validatePhoneOnCustomForm from '../../utils/validatePhoneOnCustomForm'
import validateNumberOnCustomForm from '../../utils/validateNumberOnCustomForm'
import validateNPSOnCustomForm from '../../utils/validateNPSOnCustomForm'
import validateSingleSelectionListOnCustomForm from '../../utils/validateSingleSelectionListOnCustomForm'
import validateMultipleSelectionBoxOnCustomForm from '../../utils/validateMultipleSelectionBoxOnCustomForm'
import validateSingleSelectionBoxOnCustomForm from '../../utils/validateSingleSelectionBoxOnCustomForm'

type FunctionString = {
  [key: string]: any
}

type ValidateQuestionValues = {
  formQuestions: CustomFormQuestion[]
  questionValues: any
}

export default function useValidateCustomFormQuestions() {
  function getFormatedQuestionKind(question: CustomFormQuestion) {
    const kindsThatHaveEntities = ['DOCUMENT', 'ENTITY']
    let kind = question.kind || ''
    if (kindsThatHaveEntities.includes(kind)) {
      kind = `${question.kind}_${question.entity}`
    }
    return kind
  }

  const validateQuestionFunctionByKind = useCallback((question: CustomFormQuestion) => {
    const kind = getFormatedQuestionKind(question)

    const isRequired = question.is_required

    const questions: FunctionString = {
      SHORT_TEXT: (value: string) => validateTextOnCustomForm({ text: value, question }),
      LONG_TEXT: (value: string) => validateLongTextOnCustomForm({ text: value, question }),
      EMAIL: (value: string) => validateMailOnCustomForm({ mail: value, isRequired }),
      PHONE: (value: string) => validatePhoneOnCustomForm({ phone: value, isRequired }),
      CEP: (value: string) => validateCEPOnCustomForm({ cep: value, isRequired }),
      INTEGER: (value: string) => validateNumberOnCustomForm({ number: value, question }),
      DECIMAL: (value: string) => validateNumberOnCustomForm({ number: value, question }),
      NPS: (value: string) => validateNPSOnCustomForm({ nps: value, isRequired }),
      DOCUMENT_CPF: (value: string) => validateCPFOnCustomForm({ cpf: value, isRequired }),
      DOCUMENT_CTPS: (value: string) => validateCTPSOnCustomForm({ ctps: value, isRequired }),
      DOCUMENT_PIS: (value: string) => validatePISOnCustomForm({ pis: value, isRequired }),
      SINGLE_FILE_UPLOAD: (value: string[]) => validateSingleFileUploadOnCustomForm({ file: value, question }),
      MULTIPLE_FILE_UPLOAD: (value: UploadFile[]) => validateMultipleFileUploadOnCustomForm({ files: value, question }),
      ENTITY_COST_CENTER: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_CITY: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_STATE: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_CIVIL_STATE: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_LEVEL_OF_EDUCATION: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_DEGREES_OF_KINSHIP: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_ROLE: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_UNIFIED_PERSON: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_JOBSITE: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_SALARY: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_RACE: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_PROFESSIONAL_CONTRACT_TYPE: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      ENTITY_ORGANIZATIONAL_UNIT: (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired }),
      TIME: (value: string) => validateHourOnCustomForm({ hour: value, question }),
      DATE: (value: string) => validateDateOnCustomForm({ date: value, question }),
      SINGLE_SELECTION_LIST: (value: string) => validateSingleSelectionListOnCustomForm({ text: value, isRequired }),
      SINGLE_SELECTION_BOX: (value: string) => validateSingleSelectionBoxOnCustomForm({ option: value, isRequired }),
      MULTIPLE_SELECTION_BOX: (value: string[]) => {
        return validateMultipleSelectionBoxOnCustomForm({ options: value, question })
      },
    }

    if (!questions[kind] && question.kind === 'ENTITY') {
      const entityValidateFunction = (value: string[]) => validateEntityOnCustomForm({ entity: value, isRequired })
      return entityValidateFunction
    }

    return questions[kind]
  }, [])

  async function validateQuestionValues({ formQuestions, questionValues }: ValidateQuestionValues) {
    const questionErrors: CustomFormQuestion[] = []
    formQuestions.forEach((questionItem) => {
      const validateFunction = validateQuestionFunctionByKind(questionItem)
      const questionValue = questionValues[questionItem.slug as string]
      const errorMessage = validateFunction(questionValue)
      if (errorMessage !== '') {
        questionErrors.push({ ...questionItem, error_message: errorMessage })
        focusElementById(questionItem.slug)
      }
    })
    return questionErrors
  }

  return { validateQuestionValues }
}
